import { useEffect, useMemo, useRef, useState } from 'react';
import { useGoogleMap } from '@react-google-maps/api';
import { importWithReload } from 'c-lib';
import type { GoogleMapsOverlay } from '@deck.gl/google-maps';
import type { HeatmapLayer as DeckHeatmapLayerCore } from '@deck.gl/aggregation-layers';
import type { DeckHeatmapInput, HeatmapPoint } from './heatmapPoints';
import { heatmapPoints } from './heatmapPoints';

type DeckHeatmapOptions = {
    radius?: number;
    opacity?: number;
    intensity?: number;
    threshold?: number;
    colorRange?: [number, number, number][] | [number, number, number, number][];
    aggregation?: 'SUM' | 'MEAN';
};

type Props = {
    data: DeckHeatmapInput[] | google.maps.MVCArray<DeckHeatmapInput>;
    options?: DeckHeatmapOptions;
};

type Point = HeatmapPoint;

// Classic "thermal" heatmap gradient (green -> yellow -> orange -> red)
const DEFAULT_COLOR_RANGE: [number, number, number][] = [
    [0, 255, 0],
    [76, 255, 0],
    [153, 255, 0],
    [204, 255, 0],
    [255, 255, 0],
    [255, 204, 0],
    [255, 153, 0],
    [255, 102, 0],
    [255, 51, 0],
    [255, 0, 0],
];

type LoadedDeck = {
    GoogleMapsOverlay: typeof GoogleMapsOverlay;
    HeatmapLayer: typeof DeckHeatmapLayerCore;
};

let deckPromise: Promise<LoadedDeck> | null = null;

const loadDeck = (): Promise<LoadedDeck> => {
    if (!deckPromise) {
        deckPromise = Promise.all([
            importWithReload(() => import('@deck.gl/google-maps')),
            importWithReload(() => import('@deck.gl/aggregation-layers')),
        ])
            .then(([gMaps, agg]) => ({
                GoogleMapsOverlay: gMaps.GoogleMapsOverlay,
                HeatmapLayer: agg.HeatmapLayer,
            }))
            .catch(error => {
                // Don't cache a failure: let the next render try again.
                deckPromise = null;
                throw error;
            });
    }
    return deckPromise;
};

const DeckHeatmapLayer: React.FC<Props> = ({ data, options }) => {
    const map = useGoogleMap();
    const overlayRef = useRef<GoogleMapsOverlay | null>(null);
    const heatmapCtorRef = useRef<typeof DeckHeatmapLayerCore | null>(null);
    const [ready, setReady] = useState(false);

    const radiusPixels = options?.radius ?? 60;
    const opacity = options?.opacity ?? 0.8;
    const intensity = options?.intensity ?? 1.2;
    const threshold = options?.threshold ?? 0.01;
    const colorRange = options?.colorRange ?? DEFAULT_COLOR_RANGE;
    const aggregation = options?.aggregation ?? 'SUM';

    const points = useMemo<Point[]>(() => heatmapPoints(data), [data]);

    useEffect(() => {
        if (!map) return undefined;
        let active = true;

        loadDeck()
            .then(({ GoogleMapsOverlay: OverlayCtor, HeatmapLayer }) => {
                if (!active) return undefined;
                heatmapCtorRef.current = HeatmapLayer;
                const overlay = new OverlayCtor({ layers: [] });
                overlay.setMap(map);
                overlayRef.current = overlay;
                setReady(true);
                return undefined;
            })
            .catch(error => {
                // deck.gl failed to load (a stale chunk is already handled by importWithReload).
                // The map keeps working without the heatmap. Rethrow so the failure still reaches
                // the global unhandled-rejection handler (and Sentry) exactly as before.
                throw error;
            });

        return () => {
            active = false;
            const overlay = overlayRef.current;
            if (overlay) {
                overlay.setMap(null);
                if (
                    typeof (overlay as unknown as { finalize?: () => void }).finalize === 'function'
                ) {
                    (overlay as unknown as { finalize: () => void }).finalize();
                }
            }
            overlayRef.current = null;
            heatmapCtorRef.current = null;
            setReady(false);
        };
    }, [map]);

    useEffect(() => {
        if (!ready) return;
        const overlay = overlayRef.current;
        const HeatmapCtor = heatmapCtorRef.current;
        if (!overlay || !HeatmapCtor) return;
        overlay.setProps({
            layers: [
                new HeatmapCtor({
                    id: 'heatmap-layer',
                    data: points,
                    getPosition: (d: Point) => d.position,
                    getWeight: (d: Point) => d.weight,
                    radiusPixels,
                    intensity,
                    threshold,
                    opacity,
                    aggregation,
                    colorRange,
                }),
            ],
        });
    }, [ready, points, radiusPixels, opacity, intensity, threshold, aggregation, colorRange]);

    return null;
};

export default DeckHeatmapLayer;
